import React, { useState } from 'react';
import { X } from 'lucide-react';

const ProjectModal = ({ showModal, setShowModal, project, setProject, editingProject, onSave }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!project.title.trim()) {
      newErrors.title = 'Project title is required';
    }

    if (!project.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (project.description.length < 10) {
      newErrors.description = 'Description must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const techArray = project.tech
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);

      await onSave({ ...project, tech: techArray });
      setErrors({});
    } catch (err) {
      setErrors({ general: 'Failed to save project. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setShowModal(false);
    setErrors({});
  };

  const handleInputChange = (field, value) => {
    setProject({ ...project, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  if (!showModal) return null;

  return (
    <div className="modal-backdrop" onClick={handleClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="project-modal-title"
        className="modal-panel max-w-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" onClick={handleClose} className="icon-btn absolute right-4 top-4" disabled={isLoading} aria-label="Close">
          <X size={18} strokeWidth={1.75} />
        </button>

        <h2 id="project-modal-title" className="text-xl font-semibold text-slate-50">
          {editingProject ? 'Edit Project' : 'Add New Project'}
        </h2>
        <p className="mt-2 text-sm text-slate-400">
          {editingProject ? 'Update your project details' : 'Showcase something you built'}
        </p>

        <form onSubmit={handleSubmit} className="mt-6 space-y-6" noValidate>
          <div>
            <label htmlFor="project-title" className="field-label">Project Title</label>
            <input
              id="project-title"
              type="text"
              value={project.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder="Enter the project title..."
              required
              disabled={isLoading}
              aria-invalid={Boolean(errors.title)}
              className={`field ${errors.title ? 'field-invalid' : ''}`}
            />
            {errors.title && <p className="field-error">{errors.title}</p>}
          </div>

          <div>
            <label htmlFor="project-description" className="field-label">
              Description <span className="font-mono font-normal text-slate-400">({project.description.length} characters)</span>
            </label>
            <textarea
              id="project-description"
              value={project.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Describe what the project does, the problem it solves..."
              rows="5"
              required
              disabled={isLoading}
              aria-invalid={Boolean(errors.description)}
              className={`field-area ${errors.description ? 'field-invalid' : ''}`}
            />
            {errors.description && <p className="field-error">{errors.description}</p>}
          </div>

          <div>
            <label htmlFor="project-tech" className="field-label">
              Tech Stack <span className="font-normal text-slate-400">(comma-separated)</span>
            </label>
            <input
              id="project-tech"
              type="text"
              value={project.tech}
              onChange={(e) => handleInputChange('tech', e.target.value)}
              placeholder="React, Node.js, Firebase..."
              disabled={isLoading}
              className="field"
            />
          </div>

          <div className="grid gap-6 sm:grid-cols-2">
            <div>
              <label htmlFor="project-github" className="field-label">GitHub URL</label>
              <input
                id="project-github"
                type="url"
                value={project.githubUrl}
                onChange={(e) => handleInputChange('githubUrl', e.target.value)}
                placeholder="https://github.com/..."
                disabled={isLoading}
                className="field"
              />
            </div>
            <div>
              <label htmlFor="project-live" className="field-label">Live Demo URL</label>
              <input
                id="project-live"
                type="url"
                value={project.liveUrl}
                onChange={(e) => handleInputChange('liveUrl', e.target.value)}
                placeholder="https://..."
                disabled={isLoading}
                className="field"
              />
            </div>
          </div>

          {errors.general && <p role="alert" className="form-alert">{errors.general}</p>}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={handleClose} disabled={isLoading} className="btn-secondary flex-1">
              Cancel
            </button>
            <button type="submit" disabled={isLoading} className="btn-primary flex-1">
              {isLoading
                ? (editingProject ? 'Updating...' : 'Saving...')
                : (editingProject ? 'Update Project' : 'Add Project')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectModal;
